/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import { Mail, Clock, HelpCircle, Send, MessageSquare, FileUp, Plus, Minus, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import './ContactPage.css';

const FaqItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef(null);
    return (
        <div className={`faq-item ${isOpen ? 'open' : ''}`}>
            <button className="faq-question" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen}>
                <span>{question}</span>
                <span className="faq-icon">{isOpen ? <Minus size={20}/> : <Plus size={20}/>}</span>
            </button>
            <div ref={contentRef} className="faq-answer-wrapper" style={{ maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px' }}>
                <div className="faq-answer"><p>{answer}</p></div>
            </div>
        </div>
    );
};

function ContactPage() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        consent: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });
    const [formErrors, setFormErrors] = useState({});

    const [headerRef, isHeaderVisible] = useIntersectionObserver();
    const [detailsRef, isDetailsVisible] = useIntersectionObserver();
    const [formRef, isFormVisible] = useIntersectionObserver();
    const [faqRef, isFaqVisible] = useIntersectionObserver();

    const faqData = [
        { q: "Jak szybko otrzymam odpowiedź na moje zapytanie?", a: "Staramy się odpowiadać na wszystkie zapytania najszybciej jak to możliwe, zazwyczaj w ciągu 2-4 godzin roboczych w naszych godzinach pracy (poniedziałek-piątek, 9:00 - 17:00)." },
        { q: "Jakie informacje powinienem zawrzeć w zapytaniu o wycenę?", a: "Abyśmy mogli przygotować dokładną wycenę, prosimy o załączenie plików do tłumaczenia, wskazanie pary językowej (np. z polskiego na angielski) oraz preferowanego terminu realizacji. Wszelkie dodatkowe informacje o przeznaczeniu tekstu są również mile widziane." },
        { q: "Jakie formaty plików akceptujecie?", a: "Akceptujemy większość popularnych formatów plików biurowych (DOCX, DOC, XLSX, PPTX, TXT), formaty graficzne (PDF, JPG, PNG, TIFF), pliki stron internetowych (HTML, XML) i inne. W przypadku nietypowych formatów prosimy o kontakt – postaramy się znaleźć rozwiązanie." },
        { q: "Czy korzystacie z narzędzi CAT (Computer-Assisted Translation)?", a: "Tak, wykorzystujemy nowoczesne narzędzia CAT (np. Trados Studio, memoQ), aby zapewnić spójność terminologiczną, optymalizować koszty i przyspieszyć proces tłumaczenia, zwłaszcza przy dużych i powtarzalnych projektach. Zawsze jednak kluczową rolę odgrywa tłumacz-człowiek." },
        { q: "Jakie są zasady poufności?", a: "Wszystkie przesłane materiały traktujemy jako ściśle poufne. Na życzenie klienta podpisujemy standardową umowę o zachowaniu poufności (NDA)." }
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormErrors({});
        setSubmitStatus({ type: null, message: '' });
        setIsSubmitting(true);

        let clientErrors = {};
        if (!formData.email) clientErrors.email = 'Adres e-mail jest wymagany.';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) clientErrors.email = 'Proszę podać poprawny adres e-mail.';
        if (!formData.message) clientErrors.message = 'Wiadomość jest wymagana.';
        else if (formData.message.length < 10) clientErrors.message = 'Wiadomość musi mieć co najmniej 10 znaków.';
        if (!formData.consent) clientErrors.consent = 'Zgoda jest wymagana.';

        if (Object.keys(clientErrors).length > 0) {
            setFormErrors(clientErrors);
            setIsSubmitting(false);
            const firstErrorKey = Object.keys(clientErrors)[0];
            const errorInput = document.getElementById(`contact${firstErrorKey.charAt(0).toUpperCase() + firstErrorKey.slice(1)}`) || document.getElementById('consent');
            errorInput?.focus();
            return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

        try {
            const response = await axios.post(`${apiUrl}/api/contact`, {
                name: formData.name,
                email: formData.email,
                subject: formData.subject,
                message: formData.message,
                consent: formData.consent,
            });

            setSubmitStatus({ type: 'success', message: response.data.message || 'Wiadomość wysłana pomyślnie!' });
            setFormData({ name: '', email: '', subject: '', message: '', consent: false });
            const consentCheckbox = document.getElementById('consent');
            if (consentCheckbox) consentCheckbox.checked = false;

        } catch (error) {
            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = {};
                error.response.data.errors.forEach(err => {
                    if (err.path) {
                        serverErrors[err.path] = err.msg;
                    }
                });
                if (Object.keys(serverErrors).length === 0 && error.response.data.errors.length > 0) {
                     setSubmitStatus({ type: 'error', message: error.response.data.errors[0].msg || 'Wystąpił błąd walidacji.' });
                } else {
                    setFormErrors(serverErrors);
                    const firstErrorKey = Object.keys(serverErrors)[0];
                    const errorInput = document.getElementById(`contact${firstErrorKey.charAt(0).toUpperCase() + firstErrorKey.slice(1)}`) || document.getElementById('consent');
                    errorInput?.focus();
                }
            } else if (error.request) {
                console.error("Network Error:", error.request);
                setSubmitStatus({ type: 'error', message: 'Błąd sieci. Nie można połączyć się z serwerem.' });
            } else {
                console.error("Error:", error.message);
                setSubmitStatus({ type: 'error', message: 'Wystąpił nieoczekiwany błąd.' });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (submitStatus.type) {
            const timer = setTimeout(() => {
                setSubmitStatus({ type: null, message: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [submitStatus]);

    return (
        <>
            <section ref={headerRef} className={`page-header-section section-bg-white animate-on-scroll ${isHeaderVisible ? 'is-visible' : ''}`}>
                <div className="container text-center">
                    <h1>Skontaktuj się z Nami</h1>
                    <p className="subtitle">Masz pytania, potrzebujesz indywidualnej wyceny lub chcesz omówić projekt? Jesteśmy do Twojej dyspozycji!</p>
                </div>
            </section>

            <section className="contact-content-section section-bg-light-gray">
                <div className="container">
                    <div className="contact-grid">
                        <div ref={detailsRef} className={`contact-details animate-on-scroll ${isDetailsVisible ? 'is-visible' : ''}`}>
                             <h2>Dane Kontaktowe</h2>
                             <p>Najszybszą formą kontaktu jest wysłanie wiadomości przez formularz lub bezpośrednio na nasz adres e-mail.</p>
                             <ul className="contact-list">
                                 <li><Mail size={20} strokeWidth={1.5} /><div><strong>Preferowany Kontakt:</strong><a href="mailto:biuro@lingland.pl">biuro@lingland.pl</a></div></li>
                                 <li><Clock size={20} strokeWidth={1.5} /><div><strong>Godziny Odpowiedzi:</strong><span>Poniedziałek - Piątek, 9:00 - 17:00</span><br /><small>(Staramy się odpowiadać jak najszybciej)</small></div></li>
                             </ul>
                              <div className="contact-alternative">
                                 <h3>Potrzebujesz szybkiej wyceny?</h3>
                                 <p>Skorzystaj z naszego dedykowanego formularza, aby łatwo przesłać pliki.</p>
                                 <Link to="/wycena" className="button-secondary"><FileUp size={18} style={{ marginRight: '0.5rem' }}/> Przejdź do Wyceny</Link>
                              </div>
                        </div>

                        <div ref={formRef} className={`contact-form-container animate-on-scroll ${isFormVisible ? 'is-visible' : ''}`} style={{ animationDelay: '0.2s' }}>
                             <h2>Napisz do Nas Bezpośrednio</h2>
                             <form className="contact-form" onSubmit={handleSubmit} noValidate>
                                <div className={`form-group ${formErrors.name ? 'error' : ''}`}>
                                    <label htmlFor="contactName">Imię i Nazwisko / Firma</label>
                                    <input type="text" id="contactName" name="name" placeholder="Jak możemy się do Ciebie zwracać?" value={formData.name} onChange={handleInputChange} disabled={isSubmitting} />
                                    {formErrors.name && <p className="error-message">{formErrors.name}</p>}
                                </div>
                                <div className={`form-group ${formErrors.email ? 'error' : ''}`}>
                                    <label htmlFor="contactEmail">Adres E-mail *</label>
                                    <input type="email" id="contactEmail" name="email" placeholder="Twój adres e-mail" required value={formData.email} onChange={handleInputChange} disabled={isSubmitting} aria-describedby={formErrors.email ? 'email-error' : undefined} />
                                    {formErrors.email && <p className="error-message" id="email-error">{formErrors.email}</p>}
                                </div>
                                 <div className={`form-group ${formErrors.subject ? 'error' : ''}`}>
                                    <label htmlFor="contactSubject">Temat (opcjonalnie)</label>
                                    <input type="text" id="contactSubject" name="subject" placeholder="Np. Zapytanie o tłumaczenie marketingowe" value={formData.subject} onChange={handleInputChange} disabled={isSubmitting} />
                                     {formErrors.subject && <p className="error-message">{formErrors.subject}</p>}
                                </div>
                                <div className={`form-group ${formErrors.message ? 'error' : ''}`}>
                                    <label htmlFor="contactMessage">Wiadomość *</label>
                                    <textarea id="contactMessage" name="message" rows="6" placeholder="Wpisz treść swojej wiadomości (min. 10 znaków)..." required value={formData.message} onChange={handleInputChange} disabled={isSubmitting} aria-describedby={formErrors.message ? 'message-error' : undefined}></textarea>
                                    {formErrors.message && <p className="error-message" id="message-error">{formErrors.message}</p>}
                                </div>
                                <div className={`form-group form-consent ${formErrors.consent ? 'error' : ''}`}>
                                    <div className="consent-label-wrapper" onClick={() => !isSubmitting && handleInputChange({ target: { name: 'consent', type: 'checkbox', checked: !formData.consent }})}>
                                        <input type="checkbox" id="consent" name="consent" required checked={formData.consent} onChange={handleInputChange} disabled={isSubmitting} style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }} aria-describedby={formErrors.consent ? 'consent-error' : undefined}/>
                                        <label htmlFor="consent">
                                            Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z <Link to="/polityka-prywatnosci" target="_blank" onClick={(e) => e.stopPropagation()}>Polityką Prywatności</Link>.*
                                        </label>
                                    </div>
                                     {formErrors.consent && <p className="error-message consent-error" id="consent-error">{formErrors.consent}</p>}
                                </div>

                                <div className="form-submit-area">
                                    {submitStatus.type && (
                                      <div className={`submit-message ${submitStatus.type} form-message contact-page-message`}>
                                          {submitStatus.type === 'success' ? <CheckCircle size={20}/> : <AlertCircle size={20}/>}
                                          <span>{submitStatus.message}</span>
                                      </div>
                                    )}

                                    <button type="submit" className="button-primary" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <> <Loader2 size={18} className="spinner" style={{ marginRight: '0.5rem' }}/> Wysyłanie... </>
                                        ) : (
                                            <> <Send size={18} style={{ marginRight: '0.5rem' }}/> Wyślij Wiadomość </>
                                        )}
                                    </button>
                                </div>

                                <p className="form-notice">Pola oznaczone * są wymagane.</p>
                             </form>
                        </div>
                    </div>
                </div>
            </section>

             <section ref={faqRef} className={`faq-section section-bg-white animate-on-scroll ${isFaqVisible ? 'is-visible' : ''}`}>
                <div className="container">
                    <h2 className="text-center"><HelpCircle size={28} style={{ marginRight: '0.5rem', verticalAlign: 'bottom' }}/> Często Zadawane Pytania (FAQ)</h2>
                    <div className="faq-list">
                        {faqData.map((item, index) => (<FaqItem key={index} question={item.q} answer={item.a} />))}
                    </div>
                </div>
             </section>
        </>
    );
}

export default ContactPage;